import React, { useEffect, useRef } from 'react';
import JsBarcode from 'jsbarcode';
import { Button } from './ui/button';
import { Printer, Download } from 'lucide-react';

export const BarcodeLabel = ({ 
  sku, 
  name, 
  price, 
  showPrice = true,
  width = 200,
  height = 80,
  className = '' 
}) => {
  const barcodeRef = useRef(null);

  useEffect(() => {
    if (barcodeRef.current && sku) {
      try {
        JsBarcode(barcodeRef.current, sku, {
          format: "CODE128",
          width: 1.5,
          height: 50,
          displayValue: true,
          fontSize: 12,
          margin: 5,
          background: "#ffffff",
          lineColor: "#000000"
        });
      } catch (error) {
        console.error("Barcode generation error:", error);
      }
    }
  }, [sku]);

  return (
    <div className={`bg-white p-2 border rounded ${className}`} style={{ width }}>
      <svg ref={barcodeRef}></svg>
      {name && (
        <div className="text-xs text-center mt-1 truncate font-medium">{name}</div>
      )}
      {showPrice && price !== undefined && (
        <div className="text-sm text-center font-bold text-green-600">
          ${price.toLocaleString()}
        </div>
      )}
    </div>
  );
};

export const BarcodePrintView = ({ items, onClose }) => {
  const printRef = useRef(null);

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '', 'width=800,height=600');
    const doc = printWindow.document;
    doc.open();
    doc.write('<html><head><title>Barcode Labels - JamInventra</title>');
    doc.write('<style>body { font-family: Arial, sans-serif; margin: 0; padding: 10px; } .labels-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; } .label { border: 1px dashed #ccc; padding: 8px; text-align: center; page-break-inside: avoid; } .label svg { max-width: 100%; } .item-name { font-size: 10px; margin-top: 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; } .item-price { font-size: 12px; font-weight: bold; color: #16a34a; } @media print { .labels-grid { grid-template-columns: repeat(3, 1fr); } .label { border: 1px dashed #ccc; } }</style>');
    doc.write('</head><body>');
    doc.write(printContent.innerHTML);
    doc.write('</body></html>');
    doc.close();
    
    // Generate barcodes in print window
    setTimeout(() => {
      items.forEach((item, index) => {
        const svg = printWindow.document.getElementById(`barcode-${index}`);
        if (svg) {
          try {
            JsBarcode(svg, item.sku, {
              format: "CODE128",
              width: 1.2,
              height: 40,
              displayValue: true,
              fontSize: 10,
              margin: 2
            });
          } catch (e) {
            console.error(e);
          }
        }
      });
      
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    }, 100);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-lg font-bold">Barcode Labels ({items.length} items)</h2>
          <div className="flex gap-2">
            <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700">
              <Printer className="w-4 h-4 mr-2" /> Print Labels
            </Button>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </div>
        </div>
        
        <div className="p-4 overflow-auto max-h-[calc(90vh-80px)]" ref={printRef}>
          <div className="labels-grid grid grid-cols-3 gap-3">
            {items.map((item, index) => (
              <div key={item.id || index} className="label border border-dashed border-gray-300 p-2 text-center">
                <svg id={`barcode-${index}`}></svg>
                <div className="item-name text-xs mt-1 truncate">{item.name}</div>
                {item.price && (
                  <div className="item-price text-sm font-bold text-green-600">
                    ${item.price.toLocaleString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarcodeLabel;
